import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { tokenManager } from '../api/instance';

interface BaseNotification {
  title: string;
  message: string;
  timestamp: string;
}

export interface AlertNotification extends BaseNotification {
  type: 'alert';
  alertId: string;
  isRecovery: boolean;
}

export interface BackupNotification extends BaseNotification {
  type: 'backup';
  database: string;
  fileName: string;
  success: boolean;
}

export type AppNotification = AlertNotification | BackupNotification;

interface UseNotificationsResult {
  isConnected: boolean;
  notifications: AppNotification[];
  clearNotifications: () => void;
  requestPermission: () => Promise<NotificationPermission>;
  permissionStatus: NotificationPermission | 'default';
}

const TOKEN_REFRESH_MARGIN_MS = 60_000;

async function getFreshAccessToken(): Promise<string> {
  let token = tokenManager.getAccessToken();
  if (!token) return '';

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiresAt = payload.exp * 1000;
    if (expiresAt - Date.now() < TOKEN_REFRESH_MARGIN_MS) {
      const refreshToken = tokenManager.getRefreshToken();
      if (refreshToken) {
        const response = await axios.post<{ accessToken: string; refreshToken: string }>(
          '/api/auth/refresh',
          { refreshToken }
        );
        tokenManager.setTokens(response.data.accessToken, response.data.refreshToken);
        return response.data.accessToken;
      }
    }
  } catch {
    // If token parsing or refresh fails, return current token
  }

  return token;
}

export function useNotifications(): UseNotificationsResult {
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission | 'default'>('default');
  const connectionRef = useRef<unknown>(null);
  const showBrowserNotificationRef = useRef<(notification: AppNotification) => void>(() => {});

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('Browser does not support notifications');
      return 'denied' as NotificationPermission;
    }

    const permission = await Notification.requestPermission();
    setPermissionStatus(permission);
    return permission;
  }, []);

  // Keep ref in sync with latest permissionStatus (avoids SignalR reconnect)
  showBrowserNotificationRef.current = (notification: AppNotification) => {
    if (permissionStatus !== 'granted') return;

    if (notification.type === 'alert') {
      const icon = notification.isRecovery ? '/icons/check-circle.svg' : '/icons/alert-triangle.svg';
      new Notification(notification.title, {
        body: notification.message,
        icon,
        tag: `alert-${notification.alertId}`,
        requireInteraction: !notification.isRecovery,
      });
    } else if (notification.type === 'backup') {
      const icon = notification.success ? '/icons/check-circle.svg' : '/icons/alert-triangle.svg';
      new Notification(notification.title, {
        body: notification.message,
        icon,
        tag: `backup-${notification.fileName}`,
      });
    }
  };

  useEffect(() => {
    // Check current permission status
    if ('Notification' in window) {
      setPermissionStatus(Notification.permission);
    }

    let stopped = false;

    // Dynamic import to avoid loading ~100KB signalR on every page
    import('@microsoft/signalr').then((signalR) => {
      if (stopped) return;

      // Build the hub URL based on current location
      const baseUrl = window.location.origin;
      const hubUrl = `${baseUrl}/hubs/notifications`;

      const connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => getFreshAccessToken()
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Information)
        .build();

      connectionRef.current = connection;

      connection.on('AlertNotification', (notification: Omit<AlertNotification, 'type'>) => {
        console.log('Received alert notification:', notification);

        const appNotification: AlertNotification = { ...notification, type: 'alert' };

        // Add to notifications list
        setNotifications(prev => [appNotification, ...prev].slice(0, 50)); // Keep last 50

        // Show browser notification via ref (no dependency on permissionStatus)
        showBrowserNotificationRef.current(appNotification);
      });

      connection.on('BackupNotification', (notification: Omit<BackupNotification, 'type'>) => {
        console.log('Received backup notification:', notification);

        const appNotification: BackupNotification = { ...notification, type: 'backup' };

        setNotifications(prev => [appNotification, ...prev].slice(0, 50));

        showBrowserNotificationRef.current(appNotification);
      });

      connection.onclose(() => {
        setIsConnected(false);
      });

      connection.onreconnected(() => {
        setIsConnected(true);
      });

      connection.onreconnecting(() => {
        setIsConnected(false);
      });

      // Start connection
      connection.start()
        .then(() => {
          setIsConnected(true);
          console.log('Connected to notification hub');
        })
        .catch(err => {
          console.error('Failed to connect to notification hub:', err);
        });
    }).catch(err => {
      console.error('Failed to load signalR:', err);
    });

    return () => {
      stopped = true;
      if (connectionRef.current && typeof (connectionRef.current as { stop: () => void }).stop === 'function') {
        (connectionRef.current as { stop: () => void }).stop();
      }
    };
  }, []); // No dependencies - connection is stable, notification logic uses ref

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    isConnected,
    notifications,
    clearNotifications,
    requestPermission,
    permissionStatus,
  };
}
